import { useState, useEffect, useRef } from 'react';
import { C } from '../data';
import { useAuth } from '../context/AuthContext';
import { isValidUsername, isValidName, isValidPhone, formatPhone, isValidZip, isValidAddress1, isValidPassword, PASSWORD_HINT } from '../utils/validators';
import Footer from './Footer';

const TERMS_TEXT = `UNDERLOOKED 이용약관
제1조 (목적)

본 약관은 UNDERLOOKED(이하 "회사")가 운영하는 온라인 쇼핑몰에서 제공하는 서비스의 이용과 관련하여 회사와 회원의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.

제2조 (정의)
"몰"이란 회사가 상품 또는 서비스를 회원에게 제공하기 위해 운영하는 온라인 사이트를 의미합니다.
"회원"이란 본 약관에 동의하고 회원가입을 완료한 자를 의미합니다.
"비회원"이란 회원가입 없이 서비스를 이용하는 자를 의미합니다.
제3조 (약관의 효력 및 변경)
본 약관은 회원이 동의함으로써 효력이 발생합니다.
회사는 관련 법령을 위반하지 않는 범위에서 약관을 변경할 수 있습니다.
변경된 약관은 사이트 공지사항을 통해 공지하며, 공지 후에도 서비스를 계속 이용하는 경우 변경에 동의한 것으로 간주합니다.
제4조 (회원가입)
이용자는 회사가 정한 양식에 따라 회원정보를 입력하고 약관에 동의함으로써 회원가입을 신청합니다.
회사는 다음 각 호에 해당하는 경우 회원가입을 제한할 수 있습니다.
타인의 정보를 도용한 경우
허위 정보를 기재한 경우
기타 회원가입이 부적절하다고 판단되는 경우
제5조 (회원의 의무)

회원은 다음 행위를 하여서는 안 됩니다.

허위 정보 등록
타인의 개인정보 도용
서비스 운영 방해 행위
법령 또는 공서양속에 반하는 행위
제6조 (서비스 제공)

회사는 다음 서비스를 제공합니다.

상품 정보 제공
상품 구매 및 결제 서비스
배송 서비스
고객 상담 서비스
기타 회사가 정하는 서비스
제7조 (회원 탈퇴 및 자격 상실)
회원은 언제든지 탈퇴를 요청할 수 있습니다.
회원이 약관을 위반한 경우 회사는 회원자격을 제한 또는 상실시킬 수 있습니다.
제8조 (면책조항)
회사는 천재지변 등 불가항력적인 사유로 서비스를 제공할 수 없는 경우 책임을 지지 않습니다.
회사는 회원의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다.
제9조 (준거법 및 관할법원)

본 약관은 대한민국 법률에 따라 해석되며, 서비스 이용과 관련한 분쟁은 회사 소재지 관할 법원을 전속 관할 법원으로 합니다.

※ 본 이용약관에 동의하지 않을 경우 회원가입이 제한될 수 있습니다.`;

const PRIVACY_TEXT = `개인정보 수집 및 이용 동의

UNDERLOOKED는 「개인정보 보호법」에 따라 회원가입 및 서비스 제공을 위하여 아래와 같이 개인정보를 수집·이용합니다.

1. 수집 항목
필수항목
이름
아이디(이메일)
비밀번호
휴대전화번호
선택항목
마케팅 수신 동의 여부
생년월일
성별
2. 수집 및 이용 목적
수집항목	이용목적
이름, 이메일, 휴대전화번호	회원 식별 및 본인 확인
배송정보	상품 배송 및 고객 응대
구매내역	주문 및 결제 처리
연락처	주문·배송 안내 및 고객 상담
선택정보	맞춤형 서비스 제공
3. 보유 및 이용기간

회사는 회원 탈퇴 시까지 개인정보를 보유·이용합니다.

다만, 관계 법령에 따라 아래 정보는 일정 기간 보관됩니다.

보관 항목	보관 기간
계약 또는 청약철회 기록	5년
대금결제 및 재화 공급 기록	5년
소비자 불만 및 분쟁처리 기록	3년
접속 로그	3개월
4. 동의 거부 권리

이용자는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.

다만, 필수항목 수집에 대한 동의를 거부할 경우 회원가입 및 서비스 이용이 제한될 수 있습니다.`;

export default function SignupPage({ onGoHome, onGoLogin, nav }) {
  const { signUp, checkUsernameAvailable, checkEmailAvailable } = useAuth();

  const [su, setSu] = useState({
    username: '', name: '', email: '', password: '', password2: '',
    phone: '', zip: '', address1: '', address2: '', birthdate: '',
    agreeTerms: false, agreePrivacy: false, agreeEmail: false, agreeSms: false,
  });
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [emailStatus, setEmailStatus]       = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [openTerm, setOpenTerm] = useState(null); // 'terms' | 'privacy' | null
  const usernameTimer = useRef(null);
  const emailTimer    = useRef(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  /* ── 아이디 형식 확인 + 중복 확인 (debounce) ── */
  useEffect(() => {
    if (!su.username) { setUsernameStatus(null); return; }
    if (!isValidUsername(su.username)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(async () => {
      const available = await checkUsernameAvailable(su.username.trim());
      setUsernameStatus(available ? 'available' : 'taken');
    }, 450);
    return () => clearTimeout(usernameTimer.current);
  }, [su.username, checkUsernameAvailable]);

  /* ── 이메일 형식 확인 + 중복 확인 (debounce) ── */
  useEffect(() => {
    const email = su.email.trim();
    if (!email) { setEmailStatus(null); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setEmailStatus('invalid'); return; }
    setEmailStatus('checking');
    clearTimeout(emailTimer.current);
    emailTimer.current = setTimeout(async () => {
      const available = await checkEmailAvailable(email);
      setEmailStatus(available ? 'available' : 'taken');
    }, 450);
    return () => clearTimeout(emailTimer.current);
  }, [su.email, checkEmailAvailable]);

  const setSuField = (key) => (e) => setSu(prev => ({ ...prev, [key]: e.target.value }));
  const toggleAgree = (key) => () => setSu(prev => ({ ...prev, [key]: !prev[key] }));
  const allRequiredAgreed = su.agreeTerms && su.agreePrivacy;
  const allAgreed = allRequiredAgreed && su.agreeEmail && su.agreeSms;
  const toggleAll = () => {
    const next = !allAgreed;
    setSu(prev => ({ ...prev, agreeTerms: next, agreePrivacy: next, agreeEmail: next, agreeSms: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (!su.username.trim()) throw new Error('아이디를 입력해 주세요.');
      if (!isValidUsername(su.username)) throw new Error('아이디는 영문/숫자/언더스코어(_) 6~20자로 입력해 주세요.');
      if (usernameStatus === 'taken') throw new Error('이미 사용 중인 아이디입니다.');
      if (!su.name.trim()) throw new Error('이름을 입력해 주세요.');
      if (!isValidName(su.name)) throw new Error('이름은 한글 또는 영문 2~20자로 입력해 주세요. (숫자/특수문자 불가)');
      if (!su.email.trim()) throw new Error('이메일을 입력해 주세요.');
      if (emailStatus === 'invalid') throw new Error('올바른 이메일 주소를 입력해 주세요.');
      if (emailStatus === 'taken') throw new Error('이미 가입된 이메일입니다.');
      if (emailStatus === 'checking') throw new Error('이메일 중복 확인 중입니다. 잠시 후 다시 시도해 주세요.');
      if (!isValidPassword(su.password)) throw new Error(`비밀번호는 ${PASSWORD_HINT}여야 합니다.`);
      if (su.password !== su.password2) throw new Error('비밀번호가 일치하지 않습니다.');
      if (!su.phone.trim()) throw new Error('휴대폰번호를 입력해 주세요.');
      if (!isValidPhone(su.phone)) throw new Error('휴대폰번호 형식이 올바르지 않습니다. (예: 010-1234-5678)');
      if (!su.zip.trim()) throw new Error('우편번호를 입력해 주세요.');
      if (!isValidZip(su.zip)) throw new Error('우편번호는 숫자 5자리로 입력해 주세요.');
      if (!su.address1.trim()) throw new Error('주소를 입력해 주세요.');
      if (!isValidAddress1(su.address1)) throw new Error('기본 주소를 정확히 입력해 주세요. (5자 이상, 숫자만 입력 불가)');
      if (!su.birthdate) throw new Error('생년월일을 입력해 주세요.');
      if (!allRequiredAgreed) throw new Error('필수 이용약관과 개인정보 수집·이용에 동의해 주세요.');

      const { error } = await signUp({
        username: su.username.trim(),
        name: su.name.trim(),
        email: su.email.trim(),
        password: su.password,
        phone: formatPhone(su.phone),
        zip: su.zip.trim(),
        address1: su.address1.trim(),
        address2: su.address2.trim(),
        birthdate: su.birthdate,
        agreeTerms: su.agreeTerms,
        agreePrivacy: su.agreePrivacy,
        agreeEmail: su.agreeEmail,
        agreeSms: su.agreeSms,
      });
      if (error) throw error;
      setSuccess('가입 확인 이메일을 발송했습니다. 메일함을 확인해 주세요. (가입 즉시 신규가입 쿠폰이 지급됩니다)');
    } catch (err) {
      const msg = err.message;
      setError(
        msg === 'User already registered'      ? '이미 가입된 이메일입니다.' :
        msg === 'Signups not allowed'          ? '현재 회원가입이 비활성화되어 있습니다.' :
        msg === 'email is invalid'             ? '올바른 이메일 주소를 입력해 주세요. (예: name@example.com)' :
        msg.includes('rate limit')             ? '이메일 발송 한도 초과입니다. 1시간 후 다시 시도하거나, Supabase 대시보드에서 직접 계정을 생성해 주세요.' :
        msg
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: 'var(--page-top, 100px) 24px 80px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, letterSpacing: 4 }}>UNDERLOOKED</span>
          <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 28, marginTop: 16 }}>회원가입</h1>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 14, color: '#3D8B5C', lineHeight: 2, marginBottom: 28 }}>{success}</p>
            <button onClick={onGoHome} style={{ padding: '13px 36px', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 3 }}>
              홈으로
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="adm-label">아이디</label>
              <input className="adm-input" type="text" placeholder="영문/숫자 6자 이상" value={su.username} onChange={setSuField('username')} required />
              {usernameStatus === 'invalid' && <p style={hintStyle('#C94040')}>영문/숫자/언더스코어(_) 6~20자로 입력해 주세요.</p>}
              {usernameStatus === 'checking' && <p style={hintStyle(C.muted)}>확인 중...</p>}
              {usernameStatus === 'available' && <p style={hintStyle('#3D8B5C')}>사용 가능한 아이디입니다.</p>}
              {usernameStatus === 'taken' && <p style={hintStyle('#C94040')}>이미 사용 중인 아이디입니다.</p>}
            </div>
            <div>
              <label className="adm-label">이름</label>
              <input className="adm-input" type="text" placeholder="홍길동" value={su.name} onChange={setSuField('name')} required />
            </div>
            <div>
              <label className="adm-label">이메일</label>
              <input className="adm-input" type="email" placeholder="name@example.com" value={su.email} onChange={setSuField('email')} required />
              {emailStatus === 'invalid'   && <p style={hintStyle('#C94040')}>올바른 이메일 주소를 입력해 주세요.</p>}
              {emailStatus === 'checking'  && <p style={hintStyle(C.muted)}>확인 중...</p>}
              {emailStatus === 'available' && <p style={hintStyle('#3D8B5C')}>사용 가능한 이메일입니다.</p>}
              {emailStatus === 'taken'     && <p style={hintStyle('#C94040')}>이미 가입된 이메일입니다.</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="adm-label">비밀번호</label>
                <input className="adm-input" type="password" placeholder={PASSWORD_HINT} value={su.password} onChange={setSuField('password')} required />
              </div>
              <div>
                <label className="adm-label">비밀번호 확인</label>
                <input className="adm-input" type="password" placeholder="다시 입력" value={su.password2} onChange={setSuField('password2')} required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="adm-label">휴대폰번호</label>
                <input className="adm-input" type="tel" placeholder="010-0000-0000" value={su.phone} onChange={setSuField('phone')} required />
              </div>
              <div>
                <label className="adm-label">생년월일</label>
                <input className="adm-input" type="date" value={su.birthdate} onChange={setSuField('birthdate')} required />
              </div>
            </div>
            <div>
              <label className="adm-label">우편번호</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="adm-input" style={{ maxWidth: 120 }} type="text" placeholder="12345" value={su.zip} readOnly required />
                <button type="button"
                  onClick={() => {
                    if (!window.daum?.Postcode) { alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.'); return; }
                    new window.daum.Postcode({
                      oncomplete(data) {
                        setSu(prev => ({ ...prev, zip: data.zonecode, address1: data.address }));
                      },
                    }).open();
                  }}
                  style={{ padding: '10px 14px', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 11, letterSpacing: 1, whiteSpace: 'nowrap' }}
                >주소 검색</button>
              </div>
            </div>
            <div>
              <label className="adm-label">기본 주소</label>
              <input className="adm-input" type="text" placeholder="주소 검색 후 자동 입력" value={su.address1} readOnly required style={{ background: '#F7F7F7', color: C.muted, cursor: 'default' }} />
            </div>
            <div>
              <label className="adm-label">상세 주소</label>
              <input className="adm-input" type="text" placeholder="101동 202호" value={su.address2} onChange={setSuField('address2')} />
            </div>

            {/* ── 약관 동의 ── */}
            <div style={{ border: `1px solid ${C.border}`, padding: '16px 18px', marginTop: 8 }}>
              <AgreeRow checked={allAgreed} onToggle={toggleAll} label="전체 동의" bold />
              <div style={{ height: 1, background: C.border, margin: '10px 0' }} />
              <AgreeRow checked={su.agreeTerms} onToggle={toggleAgree('agreeTerms')} label="[필수] 이용약관 동의" onView={() => setOpenTerm(o => o === 'terms' ? null : 'terms')} />
              {openTerm === 'terms' && <TermBox text={TERMS_TEXT} />}
              <AgreeRow checked={su.agreePrivacy} onToggle={toggleAgree('agreePrivacy')} label="[필수] 개인정보 수집 및 이용 동의" onView={() => setOpenTerm(o => o === 'privacy' ? null : 'privacy')} />
              {openTerm === 'privacy' && <TermBox text={PRIVACY_TEXT} />}
              <AgreeRow checked={su.agreeEmail} onToggle={toggleAgree('agreeEmail')} label="[선택] 이메일 수신 동의" />
              <AgreeRow checked={su.agreeSms} onToggle={toggleAgree('agreeSms')} label="[선택] SMS 수신 동의" />
            </div>

            {error && <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: '#C94040', textAlign: 'center', lineHeight: 1.5 }}>{error}</p>}

            <button type="submit" disabled={loading} style={{
              marginTop: 8, padding: '14px 0', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer',
              fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 3,
              opacity: loading ? .6 : 1, transition: 'opacity .18s',
            }}>{loading ? '처리 중...' : '회원가입'}</button>

            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <button type="button" onClick={onGoLogin} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: '#888' }}>
                이미 계정이 있으신가요? <span style={{ textDecoration: 'underline', color: C.ink }}>로그인</span>
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer nav={nav} />
    </>
  );
}

const hintStyle = (color) => ({ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color, marginTop: 4 });

function AgreeRow({ checked, onToggle, label, onView, bold }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={onToggle} style={{ accentColor: C.accent, width: 15, height: 15 }} />
        <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, fontWeight: bold ? 600 : 400, color: C.ink }}>{label}</span>
      </label>
      {onView && (
        <button type="button" onClick={onView} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: C.muted, textDecoration: 'underline' }}>보기</button>
      )}
    </div>
  );
}

function TermBox({ text }) {
  return (
    <pre style={{
      background: C.bg2, border: `1px solid ${C.border}`, padding: '10px 12px', margin: '4px 0 8px',
      fontFamily: "'Pretendard',sans-serif", fontSize: 10.5, color: C.muted, lineHeight: 1.7,
      whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto',
    }}>{text}</pre>
  );
}
